/**
 * F-145 / T-106-002+003 — Houston HRRB alert banner.
 * Self-contained banner shown on the Texas dashboard when the Houston
 * high-risk / Blue Star program has properties needing attention (citation
 * trigger crossed → re-registration, or Blue Star training still pending).
 * Renders nothing when the program has no pending items or the summary
 * endpoint is unreachable.
 */
import React, { useEffect, useState } from 'react';
import texasApi from '../../services/texasApi';

export interface HrrbSummaryCounts {
    needingTraining: number;
    overdueRegistration: number;
    registered: number;
}

const num = (v: any): number => Number(v) || 0;

const pick = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

export function normalizeHrrbSummary(raw: any): HrrbSummaryCounts {
    return {
        registered: num(pick(raw, ['hrrbProperties', 'hrrbCount', 'registeredProperties'])),
        needingTraining: num(pick(raw, ['propertiesNeedingTraining', 'needingTraining', 'trainingIncomplete'])),
        overdueRegistration: num(pick(raw, ['propertiesOverdueRegistration', 'overdueRegistration', 'overdue'])),
    };
}

export const HRRBBanner: React.FC = () => {
    const [counts, setCounts] = useState<HrrbSummaryCounts | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        texasApi.houstonHighRisk
            .getPortfolioSummary()
            .then((raw) => {
                if (!cancelled) setCounts(normalizeHrrbSummary(raw));
            })
            .catch(() => { /* unreachable — render nothing */ })
            .finally(() => !cancelled && setReady(true));
        return () => { cancelled = true; };
    }, []);

    const open = counts && (counts.needingTraining > 0 || counts.overdueRegistration > 0);

    if (!ready || !counts || !open) return null;

    return (
        <div className="bg-amber-500 text-white px-4 py-3 shadow-md" data-testid="hrrb-banner">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                    <p className="text-sm font-medium">
                        Houston HRRB: {counts.overdueRegistration > 0 && (
                            <span>
                                <strong>{counts.overdueRegistration}</strong>{' '}
                                {counts.overdueRegistration === 1 ? 'property' : 'properties'} at the
                                re-registration citation trigger{counts.needingTraining > 0 ? ' · ' : ''}
                            </span>
                        )}
                        {counts.needingTraining > 0 && (
                            <span>
                                <strong>{counts.needingTraining}</strong>{' '}
                                {counts.needingTraining === 1 ? 'property needs' : 'properties need'} Blue Star
                                training
                            </span>
                        )}
                    </p>
                </div>
                <a
                    href="/texas/hrrb"
                    className="bg-white text-amber-700 text-sm font-semibold px-3 py-1.5 rounded hover:bg-amber-50"
                    data-testid="hrrb-banner-cta"
                >
                    Review HRRB status
                </a>
            </div>
        </div>
    );
};

export default HRRBBanner;